import { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
import Input from '../components/Input';
import { getProfile, updateProfile } from '../services/profileService';
import { useAuth } from '../context/AuthContext';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_LENGTH = 255;

export default function Settings() {
  const { updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  // Profile state
  const [profile, setProfile] = useState({ name: '', email: '' });
  // Last saved values, used to show the header and send only changed fields
  const [savedProfile, setSavedProfile] = useState({ name: '', email: '' });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaveError, setProfileSaveError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    async function fetchProfile() {
      setProfileLoading(true);
      setProfileError('');
      try {
        const response = await getProfile();
        const data = response.data?.profile || {};
        const loaded = { name: data.name || '', email: data.email || '' };
        setProfile(loaded);
        setSavedProfile(loaded);
      } catch (err) {
        setProfileError(err.message || 'Failed to load profile.');
      } finally {
        setProfileLoading(false);
      }
    }
    fetchProfile();
  }, []);

  // Password state
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);

  function handleProfileChange(field, value) {
    setProfile((prev) => ({ ...prev, [field]: value }));
    setProfileSaveError('');
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
  }

  function validateProfile() {
    const errors = {};
    const name = profile.name.trim();
    const email = profile.email.trim();

    if (!name) {
      errors.name = 'Name is required.';
    } else if (name.length > MAX_LENGTH) {
      errors.name = `Name must be at most ${MAX_LENGTH} characters.`;
    }

    if (!email) {
      errors.email = 'Email is required.';
    } else if (email.length > MAX_LENGTH) {
      errors.email = `Email must be at most ${MAX_LENGTH} characters.`;
    } else if (!EMAIL_PATTERN.test(email)) {
      errors.email = 'Enter a valid email address.';
    }

    return errors;
  }

  async function handleProfileSave(e) {
    e.preventDefault();
    const errors = validateProfile();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setProfileSaveError('');

    // Only send fields that changed; the API rejects an empty body.
    // Email is normalised server-side, so it's sent as typed (trimmed).
    const payload = {};
    const name = profile.name.trim();
    const email = profile.email.trim();
    if (name !== savedProfile.name) payload.name = name;
    if (email !== savedProfile.email) payload.email = email;
    if (Object.keys(payload).length === 0) {
      setProfileSaveError('No changes to save.');
      return;
    }

    setProfileSaving(true);
    try {
      const response = await updateProfile(payload);
      const data = response.data?.profile || {};
      const updated = { name: data.name || name, email: data.email || email };
      setProfile(updated);
      setSavedProfile(updated);
      updateUser(updated);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2000);
    } catch (err) {
      if (err.status === 409) {
        setFieldErrors({ email: err.message || 'An account with this email already exists.' });
      } else {
        setProfileSaveError(err.message || 'Failed to update profile.');
      }
    } finally {
      setProfileSaving(false);
    }
  }

  function handlePasswordChange(field, value) {
    setPasswords((prev) => ({ ...prev, [field]: value }));
    setPasswordError('');
  }

  function handlePasswordSave(e) {
    e.preventDefault();
    if (!passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword) {
      setPasswordError('All fields are required.');
      return;
    }
    if (passwords.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setPasswordSaved(true);
    setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setTimeout(() => setPasswordSaved(false), 2000);
  }

  return (
    <div className="settings-page main-bg-top">
      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          className={`settings-tab ${activeTab === 'password' ? 'active' : ''}`}
          onClick={() => setActiveTab('password')}
        >
          Change Password
        </button>
      </div>

      {activeTab === 'profile' && profileLoading && (
        <div className="settings-content">
          <p className="password-description">Loading profile...</p>
        </div>
      )}

      {activeTab === 'profile' && !profileLoading && profileError && (
        <div className="settings-content">
          <p className="password-error">{profileError}</p>
        </div>
      )}

      {activeTab === 'profile' && !profileLoading && !profileError && (
        <div className="settings-content">
          <div className="profile-avatar-section">
            <div className="profile-avatar-large">
              <div className="profile-avatar-placeholder">
                {(savedProfile.name || savedProfile.email).charAt(0).toUpperCase()}
              </div>
            </div>
            <div className="profile-avatar-info">
              <h3>{savedProfile.name}</h3>
              <p>{savedProfile.email}</p>
            </div>
          </div>

          <form className="settings-form" onSubmit={handleProfileSave} noValidate>
            <div className="settings-form-grid">
              <div className="settings-field">
                <label className="settings-label">Name</label>
                <input
                  type="text"
                  className={`settings-input ${fieldErrors.name ? 'input-error' : ''}`}
                  value={profile.name}
                  maxLength={MAX_LENGTH}
                  onChange={(e) => handleProfileChange('name', e.target.value)}
                />
                {fieldErrors.name && <span className="input-error-text">{fieldErrors.name}</span>}
              </div>
              <div className="settings-field">
                <label className="settings-label">Email Address</label>
                <input
                  type="email"
                  className={`settings-input ${fieldErrors.email ? 'input-error' : ''}`}
                  value={profile.email}
                  maxLength={MAX_LENGTH}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                />
                {fieldErrors.email && <span className="input-error-text">{fieldErrors.email}</span>}
              </div>
            </div>
            {profileSaveError && (
              <div className="password-error">{profileSaveError}</div>
            )}
            <div className="settings-form-actions">
              <button type="submit" className="settings-save-btn" disabled={profileSaving}>
                {profileSaving ? 'Saving...' : profileSaved ? 'Saved!' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'password' && (
        <div className="settings-content">
          <div className="password-section">
            <p className="password-description">
              Update your password to keep your account secure. Make sure your new password is at least 8 characters long.
            </p>
            <form className="settings-form" onSubmit={handlePasswordSave}>
              <div className="password-form-fields">
                <Input
                  id="current-password"
                  label="Current Password"
                  type="password"
                  placeholder="Enter current password"
                  value={passwords.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                />
                <Input
                  id="new-password"
                  label="New Password"
                  type="password"
                  placeholder="Enter new password"
                  value={passwords.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                />
                <Input
                  id="confirm-password"
                  label="Confirm New Password"
                  type="password"
                  placeholder="Confirm new password"
                  value={passwords.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                />
              </div>
              {passwordError && (
                <div className="password-error">{passwordError}</div>
              )}
              <div className="settings-form-actions">
                <button type="submit" className="settings-save-btn">
                  {passwordSaved ? 'Password Updated!' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* <div className="legal-links">
        <Link to="/legal">Edit Legal Documents</Link>
        <span>·</span>
        <Link to="/termsandservices">View Terms of Service</Link>
        <span>·</span>
        <Link to="/privacypolicy">View Privacy Policy</Link>
      </div> */}
    </div>
  );
}
